<script setup lang="ts">
import { type ComponentPublicInstance, computed, onUnmounted, ref } from 'vue';
import {
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline';
import type { CaseDocument } from './useCaseDocuments';

const props = defineProps<{
  documents: CaseDocument[];
  fileId: string;
}>();

const emit = defineEmits<{
  close: [];
  'update:file-id': [fileId: string];
  // Rotation is owned by useCaseDocuments (so it survives close/reopen) —
  // this component just asks the parent to apply it.
  rotate: [fileId: string, delta: number];
}>();

// Every ready document — images and PDFs both get a full preview now;
// only PDF/octet-stream tiles that failed to load stay download-only in
// CaseDocumentsPanel.vue.
const viewable = computed(() => props.documents.filter((doc) => doc.state === 'ready'));
const index = computed(() => viewable.value.findIndex((doc) => doc.fileId === props.fileId));
const current = computed(() => (index.value >= 0 ? viewable.value[index.value] : undefined));

function isImage(doc: CaseDocument): boolean {
  return !!doc.mimeType?.startsWith('image/');
}
function isPdf(doc: CaseDocument): boolean {
  return !isImage(doc);
}

// Navigation wraps at both ends — with the document set this small,
// clamping just means an extra keypress that visibly does nothing.
function go(delta: number) {
  const n = viewable.value.length;
  if (!n || index.value < 0) return;
  const target = viewable.value[(index.value + delta + n) % n];
  emit('update:file-id', target.fileId);
}

// PDFs render via the browser's own viewer (iframe), which already has
// its own rotate/zoom — a CSS-rotated iframe would fight it and make
// scrolling/hit-testing awkward, so rotation is image-only.
function rotate(delta: number) {
  if (!current.value || isPdf(current.value)) return;
  emit('rotate', current.value.fileId, delta);
}

// Measured stage: the page box is sized in px from the stage's real
// dimensions so an image or PDF is never cropped or stretched, whichever
// way it's rotated. Falls back to a CSS aspect-ratio box before the
// first resize observation (and in environments without ResizeObserver).
const stageWidth = ref(0);
const stageHeight = ref(0);
let resizeObserver: ResizeObserver | undefined;

function attachStage(el: Element | ComponentPublicInstance | null) {
  resizeObserver?.disconnect();
  resizeObserver = undefined;
  const stageEl = el as HTMLElement | null;
  if (!stageEl || typeof ResizeObserver === 'undefined') return;
  resizeObserver = new ResizeObserver(() => {
    stageWidth.value = stageEl.clientWidth;
    stageHeight.value = stageEl.clientHeight;
  });
  resizeObserver.observe(stageEl);
  stageWidth.value = stageEl.clientWidth;
  stageHeight.value = stageEl.clientHeight;
}

onUnmounted(() => resizeObserver?.disconnect());

// Images: true aspect ratio, read once the <img> has loaded. PDFs: no
// natural size available from a blob URL, so assume A4 portrait — close
// enough for the box to look right; the page itself scrolls internally.
const PDF_RATIO = 1 / 1.414;
const naturalRatios = ref<Record<string, number>>({});

function onImageLoad(doc: CaseDocument, event: Event) {
  const img = event.target as HTMLImageElement;
  if (img.naturalWidth && img.naturalHeight) {
    naturalRatios.value[doc.fileId] = img.naturalWidth / img.naturalHeight;
  }
}

function baseRatio(doc: CaseDocument): number {
  if (isPdf(doc)) return PDF_RATIO;
  return naturalRatios.value[doc.fileId] ?? 1;
}

const docBox = computed(() => {
  const doc = current.value;
  const base =
    'position:relative; overflow:hidden; box-sizing:border-box; flex:none; ' +
    'background:#ffffff; border-radius:4px; box-shadow:0 24px 60px -12px rgba(0,0,0,.65);';
  if (!doc) return { page: base, inner: '' };

  const rotation = doc.rotation;
  const swap = rotation % 180 !== 0;
  const r = swap ? 1 / baseRatio(doc) : baseRatio(doc);

  if (!stageWidth.value || !stageHeight.value) {
    return {
      page: `${base} width:100%; aspect-ratio:${r};`,
      inner: 'width:100%; height:100%; display:block;',
    };
  }

  const w = Math.min(stageWidth.value, stageHeight.value * r);
  const h = w / r;
  const iw = swap ? h : w;
  const ih = swap ? w : h;
  return {
    page: `${base} width:${Math.round(w)}px; height:${Math.round(h)}px;`,
    inner:
      `position:absolute; top:50%; left:50%; width:${Math.round(iw)}px; ` +
      `height:${Math.round(ih)}px; transform:translate(-50%,-50%) rotate(${rotation}deg); ` +
      'transform-origin:center center;',
  };
});

// Exposed for the page's keyboard-shortcuts hook.
defineExpose({ step: go, rotate });
</script>

<template>
  <div
    v-if="current"
    class="fixed inset-0 z-[70] flex flex-col bg-gray-900/94"
    data-test="document-viewer-overlay"
    @click.self="emit('close')"
  >
    <div class="flex flex-none items-start gap-3 p-4">
      <div class="min-w-0 flex-1">
        <div class="truncate text-[13.5px] font-semibold text-white">{{ current.label }}</div>
      </div>

      <div v-if="!isPdf(current)" class="mt-[-2px] hidden flex-none items-center gap-0.5 lg:flex">
        <button
          v-if="current.rotation !== 0"
          type="button"
          data-test="btn-reset-rotation"
          class="mr-0.5 h-6 rounded-full border border-white/28 px-2 font-mono text-[10.5px] font-semibold text-white hover:bg-white/10"
          @click="rotate(-current.rotation)"
        >
          {{ current.rotation }}° · reset
        </button>
        <button
          type="button"
          data-test="btn-rotate-left"
          aria-label="Rotate left"
          title="Rotate left"
          class="grid h-11 w-11 place-items-center rounded-full text-white hover:bg-white/10"
          @click="rotate(-90)"
        >
          <ArrowUturnLeftIcon class="h-[18px] w-[18px]" />
        </button>
        <button
          type="button"
          data-test="btn-rotate-right"
          aria-label="Rotate right"
          title="Rotate right"
          class="grid h-11 w-11 place-items-center rounded-full text-white hover:bg-white/10"
          @click="rotate(90)"
        >
          <ArrowUturnRightIcon class="h-[18px] w-[18px]" />
        </button>
      </div>

      <span class="hidden flex-none self-center font-mono text-[11.5px] text-white/55 lg:block">
        {{ index + 1 }} / {{ viewable.length }}
      </span>

      <button
        type="button"
        data-test="btn-close-document-viewer"
        aria-label="Close preview"
        class="mt-[-6px] mr-[-8px] grid h-11 w-11 flex-none place-items-center rounded-md text-white hover:bg-white/10"
        @click="emit('close')"
      >
        <XMarkIcon class="h-5 w-5" />
      </button>
    </div>

    <div class="relative flex min-h-0 flex-1 items-center justify-center px-2 pb-3">
      <button
        type="button"
        data-test="btn-prev-document"
        aria-label="Previous document"
        class="absolute top-1/2 left-1.5 z-10 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/8 text-white hover:bg-white/16"
        @click="go(-1)"
      >
        <ChevronLeftIcon class="h-[22px] w-[22px]" />
      </button>

      <div
        :ref="attachStage"
        class="flex h-full w-[calc(100%-112px)] max-w-[760px] items-center justify-center lg:w-full"
      >
        <div :style="docBox.page">
          <div :style="docBox.inner">
            <img
              v-if="isImage(current)"
              data-test="document-stage-image"
              :src="current.objectUrl"
              :alt="current.label"
              class="block h-full w-full object-contain"
              @load="onImageLoad(current, $event)"
            />
            <iframe
              v-else
              data-test="document-stage-iframe"
              :src="current.objectUrl"
              :title="current.label"
              class="block h-full w-full border-0 bg-white"
            />
          </div>
        </div>
      </div>

      <button
        type="button"
        data-test="btn-next-document"
        aria-label="Next document"
        class="absolute top-1/2 right-1.5 z-10 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/8 text-white hover:bg-white/16"
        @click="go(1)"
      >
        <ChevronRightIcon class="h-[22px] w-[22px]" />
      </button>
    </div>

    <div class="flex flex-none items-center gap-1 px-3 pb-2 lg:hidden">
      <template v-if="!isPdf(current)">
        <button
          type="button"
          aria-label="Rotate left"
          class="grid h-11 w-11 flex-none place-items-center rounded-full bg-white/8 text-white"
          @click="rotate(-90)"
        >
          <ArrowUturnLeftIcon class="h-[18px] w-[18px]" />
        </button>
        <button
          type="button"
          aria-label="Rotate right"
          class="grid h-11 w-11 flex-none place-items-center rounded-full bg-white/8 text-white"
          @click="rotate(90)"
        >
          <ArrowUturnRightIcon class="h-[18px] w-[18px]" />
        </button>
        <button
          v-if="current.rotation !== 0"
          type="button"
          class="h-11 flex-none rounded-full border border-white/28 px-3.5 font-mono text-[11px] font-semibold text-white"
          @click="rotate(-current.rotation)"
        >
          {{ current.rotation }}° · reset
        </button>
      </template>
      <span class="flex-1" />
      <span class="flex-none font-mono text-[11.5px] text-white/55"
        >{{ index + 1 }} / {{ viewable.length }}</span
      >
    </div>

    <div class="flex flex-none justify-center gap-2 overflow-x-auto px-4 pb-4">
      <button
        v-for="doc in viewable"
        :key="doc.fileId"
        type="button"
        data-test="document-thumb"
        :aria-label="doc.label"
        class="w-[66px] flex-none overflow-hidden rounded-md border-[1.5px]"
        :class="
          doc.fileId === current.fileId ? 'border-white bg-white/6' : 'border-white/20 bg-white/6'
        "
        @click="emit('update:file-id', doc.fileId)"
      >
        <span
          class="grid h-11 w-full place-items-center overflow-hidden"
          :class="doc.fileId === current.fileId ? 'bg-white' : 'bg-white/72'"
        >
          <img v-if="isImage(doc)" :src="doc.objectUrl" alt="" class="h-full w-full object-cover" />
          <span v-else class="font-mono text-[9px] font-semibold tracking-wide text-gray-500"
            >PDF</span
          >
        </span>
        <span
          class="block truncate px-1.5 py-1 text-[9px] font-medium"
          :class="doc.fileId === current.fileId ? 'text-white' : 'text-white/50'"
          >{{ doc.label }}</span
        >
      </button>
    </div>
  </div>
</template>
